import { MonitoringSetupErrorSchema } from '../schemas/projectMonitoring'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { ArrowRight, CalendarClock, ShieldCheck, X } from 'lucide-react'
import { useGetMonitoringStatusQuery, useGetMonitoringSetupQuery } from '../services/sessionMonitoringApi'
import { useGetProjectQuery, useGetSecurityOwnershipQuery, useUpdateProjectMutation, useGetTestingSchedulesQuery } from '../services/projectDataApi'
import ModalPortal from './ModalPortal'
import SelfHealingSettings from './SelfHealingSettings'
import ContinuousTestingSetup from './ContinuousTestingSetup'
import { SecurityNonceSetup } from './chat/SecurityNonceChatCard'
import './chat/chat.css'
import './StartQaForm.css'
import './TestingSetup.css'

function SetupDialog({ title, subtitle, onClose, children }: { title: string; subtitle: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    const previousFocus = document.activeElement
    const panel = ref.current
    panel?.querySelector<HTMLButtonElement>('button')?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); closeRef.current(); return }
      if (event.key !== 'Tab' || !panel) return
      const controls = Array.from(panel.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea, summary, [tabindex="0"]'))
        .filter(element => !element.matches(':disabled') && element.getClientRects().length > 0)
      const first = controls[0]
      const last = controls[controls.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      if (previousFocus instanceof HTMLElement) previousFocus.focus()
    }
  }, [])
  return <ModalPortal><div className="np-overlay" onMouseDown={event => { if (event.target === event.currentTarget) onClose() }}>
    <div ref={ref} className="np-modal" role="dialog" aria-modal="true" aria-labelledby="testing-setup-title" aria-describedby="testing-setup-subtitle">
      <header className="np-modal__head"><div className="np-modal__heading"><div>
        <h2 id="testing-setup-title" className="np-modal__title">{title}</h2>
        <p id="testing-setup-subtitle" className="np-modal__lead">{subtitle}</p>
      </div></div><button type="button" className="np-modal__close" aria-label="Close setup" onClick={onClose}><X size={18} aria-hidden /></button></header>
      <div className="np-modal__body testing-setup-content">{children}</div>
    </div>
  </div></ModalPortal>
}

export default function TestingSetupActions({ projectId }: { projectId: string }) {
  const monitoring = useGetMonitoringStatusQuery(projectId, { pollingInterval: 30000, skipPollingIfUnfocused: true })
  const schedules = useGetTestingSchedulesQuery(projectId, { pollingInterval: 60000, skipPollingIfUnfocused: true })
  const [open, setOpen] = useState<'healing' | 'testing' | 'security' | null>(null)
  const projectQuery = useGetProjectQuery(projectId)
  const project = projectQuery.currentData
  const [updateProject, updating] = useUpdateProjectMutation()
  const [securityError, setSecurityError] = useState('')
  const securityEnabled = project?.enabled_polish_passes?.includes('security') === true
  const ownership = useGetSecurityOwnershipQuery({ projectId, targetUrl: project?.target_url ?? '', nonce: project?.security_nonce ?? '' }, {
    skip: !securityEnabled || project?.security_ownership_exempt === true,
    pollingInterval: 15000, skipPollingIfUnfocused: true,
  })
  const needsSecurity = securityEnabled && !project?.security_ownership_exempt && ownership.currentData?.latest?.verified !== true
  async function openSecurity() {
    setSecurityError('')
    try {
      if (!project?.security_probing_enabled || !project.security_nonce) {
        await updateProject({ id: projectId, data: { security_probing_enabled: true } }).unwrap()
      }
      setOpen('security')
    } catch { setSecurityError('Could not prepare security setup. Please try again.') }
  }
  useEffect(() => {
    if (ownership.currentData?.latest?.verified) setOpen(current => current === 'security' ? null : current)
  }, [ownership.currentData?.latest?.verified])
  const status = monitoring.currentData
  const canEdit = status?.can_edit === true
  const needsHealing = status && !status.has_sessions
  const setup = useGetMonitoringSetupQuery(projectId, { skip: !canEdit || !needsHealing })
  const needsTesting = schedules.currentData && !schedules.currentData.schedules.some(schedule => schedule.config.enabled)
  return <>
    {canEdit && (needsHealing || needsTesting || needsSecurity) && <div className="testing-setup-actions" aria-label="Set up automatic testing">
      {needsHealing && <button className="testing-setup-card" type="button" disabled={!setup.currentData} onClick={() => setOpen('healing')}>
        <span className="testing-setup-card__icon"><ShieldCheck size={22} aria-hidden /></span><span className="testing-setup-card__text"><strong>Setup Self Healing</strong><span>Watch user sessions for bugs</span></span><ArrowRight className="testing-setup-card__arrow" size={18} aria-hidden />
      </button>}
      {needsTesting && <button className="testing-setup-card" type="button" onClick={() => setOpen('testing')}>
        <span className="testing-setup-card__icon"><CalendarClock size={22} aria-hidden /></span><span className="testing-setup-card__text"><strong>Setup Continuous Testing</strong><span>Test for bugs on a schedule</span></span><ArrowRight className="testing-setup-card__arrow" size={18} aria-hidden />
      </button>}
      {needsSecurity && <button className="testing-setup-card" type="button" disabled={updating.isLoading} onClick={() => void openSecurity()}>
        <span className="testing-setup-card__icon"><ShieldCheck size={22} aria-hidden /></span><span className="testing-setup-card__text"><strong>Setup Security Testing</strong><span>Confirm ownership to start security testing</span></span><ArrowRight className="testing-setup-card__arrow" size={18} aria-hidden />
      </button>}
    </div>}
    {securityError && <p role="alert">{securityError}</p>}
    {open === 'security' && project?.security_nonce && <SetupDialog title="Confirm app ownership" subtitle="Finish setting up security testing" onClose={() => setOpen(null)}>
      <SecurityNonceSetup projectId={projectId} targetUrl={project.target_url ?? ''} nonce={project.security_nonce} verified={ownership.currentData?.latest?.verified === true} />
    </SetupDialog>}
    {needsHealing && setup.isError && <p role="alert">{MonitoringSetupErrorSchema.safeParse(setup.error).data?.data.error ?? 'Self-healing setup is unavailable.'} <button type="button" onClick={() => void setup.refetch()}>Retry</button></p>}
    {schedules.isError && <p role="alert">Could not check testing schedules. <button type="button" onClick={() => void schedules.refetch()}>Retry</button></p>}
    {open === 'healing' && <SetupDialog title="Setup Self Healing" subtitle="Watch user sessions for bugs" onClose={() => setOpen(null)}>
      <SelfHealingSettings projectId={projectId} setup />
    </SetupDialog>}
    {open === 'testing' && <SetupDialog title="Setup Continuous Testing" subtitle="Test for bugs on a schedule" onClose={() => setOpen(null)}>
      <ContinuousTestingSetup projectId={projectId} canEdit={canEdit} onDone={() => setOpen(null)} />
    </SetupDialog>}
  </>
}
